import { FC, useEffect, useState } from 'react';
import FilerobotImageEditor, { TABS, TOOLS } from 'react-filerobot-image-editor';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components/macro';

import { setErrorToast } from '../../../store/toast';
import { theme } from '../../../theme';
import { Modal } from '../../../ui/Modal';
import { isiOS, loadImage } from '../../../utils/helpers';

interface IPropsImageEditor {
  onCloseModal: () => void;
  image: string;
  onEditedImage: (file: File) => void;
}

function dataURLtoFile(dataurl: string, filename: string): File | null {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[arr.length - 1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  try {
    return new File([u8arr], filename, { type: mime });
  } catch (error) {
    console.error('Error creating File object:', error);
    return null;
  }
}

const dark_theme = {
  palette: {
    'bg-secondary': theme?.colors?.gray900,
    'txt-primary': theme?.colors?.gray0,
    'bg-primary': '#111111',
    pink: '#FFF',
    'bg-primary-active': theme?.colors?.gray950,
    'accent-primary': '#DDDDDD',
    'accent-primary-active': '#FFFFFF',
    'icons-primary': '#CCCCCC',
    'icons-secondary': '#AAAAAA',
    'borders-primary': '#CCCCCC',
    'borders-secondary': '#AAAAAA',
    'borders-strong': '#FFFFFF',
    'light-shadow': '#111111',
    warning: '#CC3333',
  },
  typography: {
    fontFamily: 'hurmeGeometricSans3, sans-serif',
  },
};

const ImageEditor: FC<IPropsImageEditor> = ({ onCloseModal, image, onEditedImage }) => {
  const { t } = useTranslation();
  const [isLoadedImage, onToggleIsLoadedImage] = useState<boolean>(false);
  useEffect(() => {}, [image]);
  const dispatch = useDispatch();
  const loadImageAsync = () => {
    if (image.includes('.mp4')) {
      dispatch(
        setErrorToast({ title: '', message: t('image_picker.You_can_just_customize_images') }),
      );
      onCloseModal();
    } else {
      loadImage(image)
        .then(() => {
          onToggleIsLoadedImage(true);
        })
        .catch(() => {
          dispatch(setErrorToast({ title: '', message: t('image_picker.there_is_no_image') }));
          onCloseModal();
        });
    }
  };

  useEffect(() => {
    loadImageAsync();
  }, [image]);

  return (
    <Modal
      classNameContent="!tw-mx-0"
      classNameHeader="!tw-mx-4"
      isShow={isLoadedImage}
      title={t('image_picker.customize')}
      onCloseModal={onCloseModal}
    >
      <Wrapper className={`tw-mt-4 ${isiOS ? 'is-ios' : ''} `}>
        <FilerobotImageEditor
          theme={dark_theme}
          source={image}
          disableSaveIfNoChanges={false}
          defaultSavedImageType="jpeg"
          onSave={(editedImageObject, designState) => {
            onEditedImage(dataURLtoFile(editedImageObject.imageBase64 as string, 'test') as File);
          }}
          disableZooming={true}
          onClose={() => {}}
          savingPixelRatio={2} // Add this property
          previewPixelRatio={1} // Add this property
          Text={{
            text: '',
            fonts: ['Tahoma'],
          }}
          Crop={{
            presetsItems: [
              {
                titleKey: 'classicTv',
                descriptionKey: '4:3',
                ratio: 4 / 3,
              },
            ],
            autoResize: false,
          }}
          tabsIds={[TABS.ADJUST, TABS.ANNOTATE]}
          translations={{
            save: t('image_picker.save'),
            cancel: t('image_picker.close'),
            warning: t('image_picker.warning'),
            confirm: t('image_picker.confirm'),
            addImage: t('image_picker.add_image'),
            changesLoseWarningHint: t('image_picker.changes_lose_warning_hint'),
          }}
          Image={{
            disableUpload: true,
            gallery: [
              {
                originalUrl: '/static/svg/logo.svg',
                previewUrl: '/static/svg/logo.svg',
              },
              {
                originalUrl: '/static/svg/whit-logo.svg',
                previewUrl: '/static/svg/whit-logo.svg',
              },
              {
                originalUrl: '/static/svg/orange-circle.svg',
                previewUrl: '/static/svg/orange-circle.svg',
              },
              {
                originalUrl: '/static/svg/red-circle.svg',
                previewUrl: '/static/svg/red-circle.svg',
              },
              {
                originalUrl: '/static/svg/yellow-circle.svg',
                previewUrl: '/static/svg/yellow-circle.svg',
              },
              {
                originalUrl: '/static/svg/orange-suqare.svg',
                previewUrl: '/static/svg/orange-suqare.svg',
              },
              {
                originalUrl: '/static/svg/red-square.svg',
                previewUrl: '/static/svg/red-square.svg',
              },
              {
                originalUrl: '/static/svg/yellow-suqare.svg',
                previewUrl: '/static/svg/yellow-suqare.svg',
              },
              {
                originalUrl: '/static/svg/black-circle.png',
                previewUrl: '/static/svg/black-circle.png',
              },
            ],
          }}
          avoidChangesNotSavedAlertOnLeave={true}
          onBeforeSave={() => false}
          defaultTabId={TABS.ANNOTATE}
          defaultToolId={TOOLS.IMAGE}
        />
      </Wrapper>
    </Modal>
  );
};

export default ImageEditor;

const Wrapper = styled.div`
  width: 50vw;

  @media only screen and ${({ theme }) => theme.breakpoints.tablet} {
    width: 100%;

    height: 80vh;
    &.is-ios {
      height: 70vh;
    }

    @media (display-mode: standalone) {
      height: 80vh;
    }

    .FIE_canvas-node > div {
      width: 100vw !important;
      height: auto !important;

      canvas {
        width: 100% !important;
        height: auto !important;
        //object-fit: ;
      }
    }
  }
  .FIE_canvas-container {
    padding: 0;
  }
  .FIE_tools-items li {
    padding: 0;
    label {
      display: none;
    }
  }
  .FIE_canvas-node > div,
  .FIE_topbar > div > div,
  .FIE_spinner-wrapper {
    background-color: ${({ theme }) => theme.colors.gray900};
  }

  .FIE_spinner-wrapper {
    path {
      fill: ${({ theme }) => theme.colors.green500};
    }
  }

  .SfxDrawer-item > div {
    background-color: ${({ theme }) => theme.colors.gray950};
  }
  .FIE_tabs-drawer {
    background-color: ${({ theme }) => theme.colors.gray900};
  }

  .FIE_topbar-save-button {
    background-color: ${({ theme }) => theme.colors.green500};
    color: ${({ theme }) => theme.colors.gray0};
  }

  .FIE_image-tool-add-option-button {
    background-color: ${({ theme }) => theme.colors.gray400};
    color: ${({ theme }) => theme.colors.gray0};
    border: none;
  }

  .FIE_text-size-option {
    color: ${({ theme }) => theme.colors.gray900};
  }

  .FIE_topbar {
    border: 0;
  }
  .FIE_rect-tool-button,
  .FIE_text-bold-option,
  .FIE_text-italic-option,
  .FIE_ellipse-tool-button,
  .FIE_polygon-tool-button,
  .FIE_rotate-tool-button,
  .FIE_topbar-close-button,
  .FIE_topbar-center-options,
  .SfxLabel-text,
  [title='Opacity'],
  [title='Position'],
  [title='Stroke'],
  [title='Shadow'],
  [title='Text spacings'] {
    display: none !important;
  }

  .FIE_tool-options-wrapper {
    min-height: 58px;
  }
`;
